import { createTheme } from '@mui/material/styles';

/**
 * 애플리케이션 전체의 Material UI 테마 설정
 * 다국어(몽골어, 러시아어, 영문 등) 및 한글의 완벽한 폰트 렌더링을 위해
 * 시스템 표준 폰트(-apple-system, BlinkMacSystemFont, Segoe UI, Roboto)를 최우선 폰트로 설정합니다.
 */

const fontStack = [
  '"Noto Sans KR"',
  '"Noto Sans"',
  '-apple-system',
  'BlinkMacSystemFont',
  '"Segoe UI"',
  'Roboto',
  '"Helvetica Neue"',
  'Arial',
  'sans-serif',
  '"Apple Color Emoji"',
  '"Segoe UI Emoji"',
  '"Segoe UI Symbol"',
].join(',');

const theme = createTheme({
  palette: {
    primary: {
      main: '#1565C0',
      light: '#42A5F5',
      dark: '#0D47A1',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#37474F',
      light: '#62727B',
      dark: '#263238',
      contrastText: '#ffffff',
    },
    success: {
      main: '#2E7D32',
      light: '#66BB6A',
      dark: '#1B5E20',
    },
    warning: {
      main: '#F57C00',
      light: '#FFB74D',
      dark: '#E65100',
    },
    error: {
      main: '#C62828',
      light: '#EF5350',
      dark: '#B71C1C',
    },
    info: {
      main: '#0277BD',
      light: '#03DAC6',
      dark: '#01579B',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#212121',
      secondary: '#616161',
      disabled: '#9E9E9E',
    },
    divider: '#E0E0E0',
    neutral: {
      50: '#FAFAFA',
      100: '#F5F5F5',
      200: '#EEEEEE',
      300: '#E0E0E0',
      400: '#BDBDBD',
      500: '#9E9E9E',
      600: '#757575',
      700: '#616161',
      800: '#424242',
      900: '#212121',
    },
  },
  
  typography: {
    fontFamily: fontStack,
    h1: {
      fontFamily: fontStack,
      fontWeight: 700,
      letterSpacing: '-0.01562em',
      color: '#212121',
    },
    h2: {
      fontFamily: fontStack,
      fontWeight: 700,
      letterSpacing: '-0.00833em',
      color: '#212121',
    },
    h3: {
      fontFamily: fontStack,
      fontWeight: 600,
      letterSpacing: '0em',
      color: '#212121',
    },
    h4: {
      fontFamily: fontStack,
      fontWeight: 600,
      letterSpacing: '0.00735em',
      color: '#212121',
    },
    h5: {
      fontFamily: fontStack,
      fontWeight: 500,
      letterSpacing: '0em',
      color: '#212121',
    },
    h6: {
      fontFamily: fontStack,
      fontWeight: 500,
      letterSpacing: '0.0075em',
      color: '#212121',
    },
    body1: {
      fontFamily: fontStack,
      color: '#212121',
    },
    body2: {
      fontFamily: fontStack,
      color: '#616161',
    },
    button: {
      fontFamily: fontStack,
      fontWeight: 500,
    },
    caption: {
      fontFamily: fontStack,
      color: '#757575',
    },
    overline: {
      fontFamily: fontStack,
      color: '#757575',
    },
  },
  
  shape: {
    borderRadius: 8,
  },
  
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: '6px',
          fontWeight: 500,
          padding: '10px 24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          fontFamily: fontStack,
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease',
        },
        contained: {
          background: '#1565C0',
          color: '#ffffff',
          '&:hover': {
            background: '#0D47A1',
          },
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#1565C0',
          color: '#1565C0',
          '&:hover': {
            borderWidth: '1px',
            borderColor: '#0D47A1',
            background: 'rgba(21, 101, 192, 0.04)',
          },
        },
        text: {
          color: '#1565C0',
          '&:hover': {
            background: 'rgba(21, 101, 192, 0.04)',
          },
        },
      },
    },
    
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(90deg, #263238 0%, #37474F 100%)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          fontFamily: fontStack,
        },
      },
    },
    
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
          borderRadius: '8px',
          border: '1px solid #E0E0E0',
          transition: 'all 0.2s ease',
          fontFamily: fontStack,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          },
        },
      },
    },
    
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontFamily: fontStack,
        },
        elevation1: {
          boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)',
        },
        elevation3: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
        },
      },
    },
    
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '6px',
          fontWeight: 500,
          fontFamily: fontStack,
        },
        filled: {
          backgroundColor: '#E3F2FD',
          color: '#1565C0',
        },
        outlined: {
          borderWidth: '1px',
          borderColor: '#1565C0',
          color: '#1565C0',
        },
      },
    },
    
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    
    MuiTextField: {
      styleOverrides: {
        root: {
          fontFamily: fontStack,
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
            fontFamily: fontStack,
            '& input': {
              fontFamily: fontStack,
              letterSpacing: 'normal',
            },
            '& textarea': {
              fontFamily: fontStack,
              letterSpacing: 'normal',
            },
            '&:hover fieldset': {
              borderColor: '#1565C0',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1565C0',
            },
          },
          '& .MuiInputLabel-root': {
            fontFamily: fontStack,
            '&.Mui-focused': {
              color: '#1565C0',
            },
          },
        },
      },
    },
    
    MuiTypography: {
      styleOverrides: {
        root: {
          fontFamily: fontStack,
        },
      },
    },
    
    MuiListItemText: {
      styleOverrides: {
        root: {
          fontFamily: fontStack,
        },
      },
    },
    
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontFamily: fontStack,
        },
      },
    },
    
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FAFAFA',
          borderRight: '1px solid #E0E0E0',
        },
      },
    },
    
    MuiMenu: {
      styleOverrides: {
        paper: {
          boxShadow: '0 3px 6px rgba(0,0,0,0.16), 0 3px 6px rgba(0,0,0,0.23)',
          border: '1px solid #E0E0E0',
        },
      },
    },
    
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#F5F5F5',
        },
      },
    },
    
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 600,
          color: '#212121',
          fontFamily: fontStack,
        },
        body: {
          fontFamily: fontStack,
        },
      },
    },
  },
});

export default theme;